// --- Libraries
import fs from 'fs';
import microprofiler from 'microprofiler';
import os from 'os';
import request from 'requestretry';
// --- Modules
import configure from '../configure';

// ASCII ESCAPE SEQUENCE http://www5c.biglobe.ne.jp/~ecb/assembler2/b_2.html
const levelToColor = {
  '6': '[N]', // no color
  v: '[35m[V]', // purple
  d: '[34m[D]', // blue
  w: '[33m[W]', // yellow
  '1': '[31m[E]'  // red
};

export const LEVELS = {
  'ERROR': 0,
  'WARNING': 1,
  'INFO': 2,
  'DEBUG': 3
};

const LEVEL_COLORS = {
  'DEBUG': '[35m[V]', // purple
  'INFO': '[34m[D]', // blue
  'WARNING': '[33m[W]', // yellow
  'ERROR': '[31m[E]'  // red
};

function getFormattedDate() {
  const d = new Date();
  let month = d.getMonth() + 1;
  if (month < 10) month = `0${month}`;
  let date = d.getDate();
  if (date < 10) date = `0${date}`;
  let hour = d.getHours();
  if (hour < 10) hour = `0${hour}`;
  let minutes = d.getMinutes();
  if (minutes < 10) minutes = `0${minutes}`;
  let sec = d.getSeconds();
  if (sec < 10) sec = `0${sec}`;
  return `${d.getFullYear()}-${month}-${date}T${hour}:${minutes}:${sec}.${d.getMilliseconds()}Z`;
}

/**
 * Logger for use with roar-sdk
 *
 * format:
 * ``[LogLevel] [Time] [File] log text``
 *
 * log levels:
 * - verbose
 * - debug
 * - info
 * - warning
 * - error
 *
 * @example
 * import { Logger } from 'roar-sdk'
 *
 * // simple usage
 * Logger.verbose('verbose log');
 *
 */
class Logger {
  constructor() {
    this.profiles = {};
  }

  /**
   * Get log information.
   * @return {String} Info string containing the file name and line number.
   * @private
   */
  getInfo() {
    let info;
    try {
      throw new Error();
    } catch (e) {
      const lines = e.stack.split('\n');
      const line = lines[4];
      const matched = line.match(/([\w\d\-_.]*:\d+:\d+)/);
      info = matched[1];
    }

    return info;
  }

  log(uid, key, options, cb) {//level, msg, status) {
    const info = this.getInfo();

    if (process.env.NODE_ENV === 'dev' || process.env.NODE_ENV === 'test') {
      const color = LEVEL_COLORS[`${options.level || 'INFO'}`];
      const log = `${color} [${configure.version}] [${getFormattedDate()}] [${info}] ${options.msg || ''}[0m`;
      console.log(log);
    } else {
      request({
        method: 'POST',
        url: `${configure.apiURL()}/job/${uid}`,
        json: true,
        body: options,
        gzip: true,
        maxAttempts: 1,   // (default) try 5 times
        retryDelay: 20000,  // (default) wait for 20s before trying again
        retryStrategy: request.RetryStrategies.HTTPOrNetworkError, // (default) retry on 5xx or network errors
        headers: {
          'Authorization': key,
          'Accept': 'application/json'
        }
      }, (error, response, body) => {
        if (error) {
          console.error(`[WARN] [${configure.version}] [${new Date().toLocaleString()}] [${info}] Failed to send log messge to Roar API`, error);
        }

        if (cb) {
          cb();
        }
      });
    }
  }

  /**
   * Displays the log
   * @param {String} level The log level, v, d, i, w, e
   * @param {...*} msg The message to log
   * @returns {String} The formatted log message.
   * @private
   */
  output(level, msg, details, msgCode, extra) {
    const info = this.getInfo();

    if (process.env.NODE_ENV === 'dev' || process.env.NODE_ENV === 'test') {
      const color = levelToColor[`${level}`];
      const log = `${color} [${configure.version}] [${getFormattedDate()}] [${info}] ${details || ''}[0m`;
      console.log(log);
    } else {
      let log = {
        'version': configure.version,
        'host': require('os').hostname(),
        'short_message': msg,
        'full_message': details || '',
        'timestamp': new Date().getTime() / 1000,
        'level': level,
        'facility': 'roar-agent',
        'file': info ? info[0] : 'N/A',
        'line': info ? info[1] : 'N/A',
        '_msgCode': msgCode
      };

      if (extra) {
        log = Object.assign(log, extra);
      }

      request({
        method: 'POST',
        url: `${configure.apiURL()}/sdk/log`,
        json: true,
        body: log,
        gzip: true,
        maxAttempts: 1,   // (default) try 5 times
        retryDelay: 20000,  // (default) wait for 20s before trying again
        retryStrategy: request.RetryStrategies.HTTPOrNetworkError, // (default) retry on 5xx or network errors
        headers: {
          'Authorization': global.ACCESS_KEY,
          'Accept': 'application/json'
        }
      }, (error, response, body) => {
        fs.appendFileSync('./scanners.log', JSON.stringify(log));
        if (error) {
          console.error(`[WARN] [${configure.version}] [${getFormattedDate()}] [${info}] Failed to send log messge to Roar API`, error);
        }
      });
    }
  }

  /**
   * Logs a verbose message
   * @param {...*} msg Message to log
   * @returns {string} Formatted log message.
   */
  verbose(...msg) {
    return this.output('v', ...msg);
  }

  /**
   * Logs a debug message
   * @param {...*} msg Message to log
   * @returns {string} Formatted log message.
   */
  debug(...msg) {
    return this.output('d', ...msg);
  }

  /**
   * Logs an informative message
   * @param {...*} msg Message to log
   * @returns {string} Formatted log message.
   */
  info(...msg) {
    return this.output('i', ...msg);
  }

  /**
   * Logs a warning message
   * @param {...*} msg Message to log
   * @returns {string} Formatted log message.
   */
  warn(...msg) {
    return this.output('w', ...msg);
  }

  /**
   * Logs an error message
   * @param {...*} msg Message to log
   * @returns {string} Formatted log message.
   */
  error(...msg) {
    return this.output('e', ...msg);
  }

  /**
   * Starts profiling the code block to follow
   * @param {String} tag Tag to identify the code block by
   */
  startProfile(tag) {
    this.profiles[tag] = microprofiler.start();
  }

  /**
   * Ends profiling the previous code block
   * @param {String} tag Tag to identify the code block to stop profiling
   */
  endProfile(tag) {
    microprofiler.measureFrom(this.profiles[tag], tag);
  }
}

const logger = new Logger();
export default logger; // our default logger
